'use client';

import React, { useEffect } from 'react';
import { X, AlertCircle, CheckCircle, Info, AlertTriangle } from 'lucide-react';

type AlertVariant = 'error' | 'warning' | 'success' | 'info';

interface AlertModalProps {
  isOpen: boolean;
  onClose: () => void;
  message: string;
  title?: string;
  variant?: AlertVariant;
  onConfirm?: () => void;
  confirmText?: string;
  cancelText?: string;
}

const variantConfig: Record<AlertVariant, { icon: React.ReactNode; accent: string; bg: string; border: string }> = {
  error: {
    icon: <AlertCircle className="w-6 h-6 text-rose-500" />,
    accent: 'text-rose-600',
    bg: 'bg-rose-50',
    border: 'border-rose-200',
  },
  warning: {
    icon: <AlertTriangle className="w-6 h-6 text-amber-500" />,
    accent: 'text-amber-600',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
  },
  success: {
    icon: <CheckCircle className="w-6 h-6 text-emerald-500" />,
    accent: 'text-emerald-600',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
  },
  info: {
    icon: <Info className="w-6 h-6 text-indigo-500" />,
    accent: 'text-indigo-600',
    bg: 'bg-indigo-50',
    border: 'border-indigo-200',
  },
};

export const AlertModal: React.FC<AlertModalProps> = ({
  isOpen,
  onClose,
  message,
  title,
  variant = 'warning',
  onConfirm,
  confirmText,
  cancelText,
}) => {
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const { icon, accent, bg, border } = variantConfig[variant];

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div
        onClick={onClose}
        className="absolute inset-0 bg-slate-950/40 backdrop-blur-sm animate-fade-in"
      />
      <div className="relative bg-white rounded-2xl border border-slate-200 shadow-2xl max-w-sm w-full animate-fade-in">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-slate-400 hover:text-slate-700 p-1.5 hover:bg-slate-100 rounded-lg transition-colors focus:outline-none"
          aria-label="Cerrar"
        >
          <X className="w-4 h-4" />
        </button>

        <div className={`flex items-start gap-4 p-6 rounded-2xl ${bg} ${border} border`}>
          <div className="flex-shrink-0 mt-0.5">{icon}</div>
          <div className="flex-1 min-w-0 pr-4">
            {title && (
              <p className={`text-sm font-extrabold font-heading mb-1 ${accent}`}>{title}</p>
            )}
            <p className="text-sm text-slate-700 leading-relaxed">{message}</p>
          </div>
        </div>

        <div className="px-6 pb-5 pt-4 flex justify-end gap-2">
          {onConfirm ? (
            <>
              <button
                onClick={onClose}
                className="px-4 py-2 border border-slate-200 hover:bg-slate-150 text-slate-600 hover:text-slate-800 text-xs font-bold rounded-xl transition-colors focus:outline-none"
              >
                {cancelText || 'Volver'}
              </button>
              <button
                onClick={onConfirm}
                className="px-5 py-2 bg-blue-500 hover:bg-blue-600 text-white text-xs font-bold rounded-xl shadow-md transition-colors focus:outline-none"
              >
                {confirmText || 'Sí, vaciar'}
              </button>
            </>
          ) : (
            <button
              onClick={onClose}
              className="px-5 py-2 bg-slate-900 hover:bg-slate-700 text-white text-xs font-bold rounded-xl transition-colors focus:outline-none"
            >
              Entendido
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AlertModal;
